import { apiUrl } from '../Utility/ApiUrl'
import { debounce } from '../Utility/Debounce'

const DEBOUNCE_DELAY = 250
const FLIP_OUT_DURATION = 150
const COUNT_ENDPOINT = '/api/course-search/count'
const PLUGIN_PREFIX = 'tx_sitepackage_coursesearch'
const PLUGIN_PREFIX_PATTERN = new RegExp(
  `^${PLUGIN_PREFIX}\\[(filter|q|removeFilter)\\](.*)$`
)

interface CountResponse {
  count?: number
}

// Updates the "N Treffer" submit-button labels live as the user toggles filter
// checkboxes — without reloading the result list. The full reload happens only
// when the user actually clicks a submit button.
class CourseFilterLiveCount {
  private readonly forms: NodeListOf<HTMLFormElement>

  constructor() {
    this.forms = document.querySelectorAll<HTMLFormElement>('[data-js="search-filter-form"]')
  }

  public init(): void {
    this.forms.forEach((form): void => {
      this.bindForm(form)
    })
  }

  // Announce the new count to screen readers. On the hero there is no result
  // list (CourseFilterLive does not run), so the polite live region is the only
  // way a non-visual user perceives that a filter changed the result count.
  private announce(count: number): void {
    const announcer = document.querySelector<HTMLElement>('[data-js="course-search-announcer"]')
    if (!announcer) return
    announcer.textContent = `${count} Fortbildungen gefunden`
  }

  private bindForm(form: HTMLFormElement): void {
    // Exclude the mobile-only magnifier icon span (`__submit-icon`) — it is a
    // mask-only icon, so writing the count into it would render stray text next
    // to the icon. Matches the CSS flip selector in CourseSearch.scss.
    const submitTargets = form.querySelectorAll<HTMLElement>(
      '.search-filter__submit > span:not(.search-filter__submit-icon), .search-filter__panels-submit'
    )
    if (submitTargets.length === 0) return

    const update = debounce((): void => {
      this.fetchAndApply(form, submitTargets)
    }, DEBOUNCE_DELAY)

    form.addEventListener('change', (event): void => {
      const target = event.target as HTMLElement | null
      // Skip the "X-remove" applied-chip buttons and search submit-buttons —
      // those navigate immediately, no preview needed.
      if (target?.matches('button[type="submit"]')) return

      update()
    })
  }

  private buildCountUrl(form: HTMLFormElement): string {
    // Strip the `tx_sitepackage_coursesearch[…]` Extbase namespace from form
    // field names — the middleware reads plain `filter[…]`, `q`, `removeFilter[…]`.
    // Skip applied-chip remove buttons: the preview should reflect the CURRENT
    // checkbox state only, removals are applied on real submit.
    const params = new URLSearchParams()
    new FormData(form).forEach((value, key): void => {
      const match = PLUGIN_PREFIX_PATTERN.exec(key)
      if (!match) return
      const [, group, rest] = match
      if (group === 'removeFilter') return
      params.append(`${group}${rest}`, value.toString())
    })

    return `${apiUrl(COUNT_ENDPOINT)}?${params.toString()}`
  }

  private async fetchAndApply(
    form: HTMLFormElement,
    targets: NodeListOf<HTMLElement>
  ): Promise<void> {
    const url = this.buildCountUrl(form)

    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      })
      if (!response.ok) return

      const data: CountResponse = await response.json()
      if (typeof data.count !== 'number') return

      const nextText = `${data.count} Treffer`
      targets.forEach((target): void => {
        this.flipText(target, nextText)
      })
      this.announce(data.count)
    } catch {
      // Network error — keep the existing label, the user can still submit.
    }
  }

  // Two-phase transition: fade old text out (CSS), swap text, let CSS fade in.
  // Skips the animation when text would not change.
  private flipText(target: HTMLElement, nextText: string): void {
    if ((target.textContent ?? '').trim() === nextText) return

    target.classList.add('is-flipping')
    window.setTimeout((): void => {
      target.textContent = nextText
      target.classList.remove('is-flipping')
    }, FLIP_OUT_DURATION)
  }
}

const liveCount = new CourseFilterLiveCount()
liveCount.init()
